import isNumber from 'lodash/isNumber';
import type { CompleteType} from '../util/general';
import { isEnum } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { TranscriptRuleSetting } from '../transcript/transcriptParametricRules';
import { RestoreTier } from './restoreMediaRequestDto';
import type { TenantTimeFormat } from '../util/DateUtil';
import type { PersonDetectionParams } from './asyncOperationPrimitives';
import type { ItemTranscriptionSettings } from './asyncOperationModels';
import type { TextDetectionSettings } from './textDetectionSettings';
import type { TenantServiceRate } from './serviceRate';
import type { DefaultMapView } from './GeoLocation';
import type { FeatureFlag } from '../util/featureSet';
import type { CloudLiveConfig } from './cloudLiveConfig';
import type { editProxyFpss} from './editProxyFps';
import { validateEditProxyFps } from './editProxyFps';
import type { SupportedVideoConversionFieldOrders } from './mediaDtos';
import type { HttpRequestHeaders } from '../util/apiClient';
import type { ItemMetaData } from './fieldValue';
import type { H264Preset } from '../transcoding/codecs';
import type { AudioDownMix } from './audioMixingDtos';
import type { AnalyticsPluginConfig, CloudEncodingFormat, EditProxyFormat } from './itemPropTypes';
import type { CustomActionDto } from './customActionDtos';
import type { CustomHeadersType } from './customHeadersDto';
import type { CustomIconRulesDto, CustomIconDto, CustomIconSlotRuleDto } from './customIconDtos';
import {
  makeValidationFunction,
  validateOptionalStringProperty,
  validateStringProperty
} from '@mimir/common/util/validation';
import { itemCustomIconSlots } from '@mimir/common/dto/extendedItemDto';
import { isInstanceOfFraction, type Fraction } from '../util/fractionType';

// Crop image save dialog option values
export const CROP_SAVE_DIALOG_OPTIONS = {
  SAVE: 'save',
  SAVE_NEW_VERSION: 'saveNewVersion',
} as const;
import type { PluginDto } from './pluginDto';

const DEFAULT_ON_PREM_EDIT_PROXY_GEN_COST = 0.5;
export const DEFAULT_CRASH_RECORDING_DURATION_MINUTE = 120;
export const DEFAULT_CRASH_RECORDING_NAME = 'New recording {c} {yyyymmdd} - {hhmm}';
export const DEFAULT_SERIES_ITEM_CREATION_WINDOW_MINUTE = 30;
export const DEFAULT_FACE_DETECTION_MINIMUM_WIDTH_RATIO = 0.02;

// Crop image save dialog type
type CropImageSaveDialogOption = typeof CROP_SAVE_DIALOG_OPTIONS[keyof typeof CROP_SAVE_DIALOG_OPTIONS];

export interface TenantIdsDto {
  ids: string[];
}

export interface TenantSlimDto {
  id: string;
  domainSuffix: string;
  createdOn: string;
}

export interface CreateTenantRequestDto {
  team: string;
  email: string;
  config?: CreateTenantConfigDto;
  /**
   * If the requested tenant subscribed to Mimir from the AWS marketplace
   */
  awsMarketplaceSubscriberId?: string;
  markUpFactor?: number;
}

export default interface TenantModel {
  id: string;
  domainSuffix: string;
  createdOn: string;
  createdBy: string;
  config?: TenantConfigDto;
  transcriptionLanguageSettings?: any[];
  mdfOrder?: string[];
}

export interface Availability {
  data: {
    isTaken: boolean;
    message?: string;
  }
}

export const getDefaultGroupId = (tenantId: string) => `${tenantId}:organization`;

export const defaultAnalyticConfig = (): CompleteType<AnalyticConfig> => {
  return {
    transcriptionEnabled: false,
    labelDetectionEnabled: false,
    textDetectionEnabled: false,
    personDetectionEnabled: false,
    celebrityDetectionEnabled: false,
    transcriptionConfig: undefined,
    textDetectionSettings: undefined,
    personDetectionConfig: undefined,
    analyticPlugins: undefined,
  };
};

export function defaultShareLimits(): TenantConfigDto['shareLimits'] {
  return {
    maxValidDays: 10,
    maxViews: 50,
  };
}

export const defaultEditProxyConfig = (): EditProxyConfig => {
  return {
    enabled: false,
    format: 'h264',
    profile: undefined,
    bitrate: undefined,
    maxBitrate: undefined,
    audioBitrate: undefined,
    fps: undefined,
    gopSize: undefined,
    par: '1/1',
    maxHeight: 1080,
    mergeAudioStreams: false,
    alphaResolutionMode: 'source',
  };
};

export const defaultServiceRate = (): TenantServiceRate => {
  return {
    mimirServices: {
      editProxyGenCost: {
        onPrem: DEFAULT_ON_PREM_EDIT_PROXY_GEN_COST
      },
      keldaTranscodingCost: undefined,
      keldaAudioTranscodingCost: undefined,
    },
    markup: undefined,
    profitPercentage: undefined,
  };
};

export function defaultCustomIconRules(): CustomIconRulesDto {
  const result: CustomIconRulesDto = {} as any;

  for (const slot of itemCustomIconSlots) {
    result[slot] = null;
  }

  return result;
}

export function getHttpRequestHeadersFromCustomHeaders(customHeaders: CustomHeadersType) {
  const headers: HttpRequestHeaders = {};
  for (const header of customHeaders) {
    const customHeaderField = header.headerField;
    const customHeaderValue = header.headerValue;
    if (customHeaderField && customHeaderValue) {
      headers[customHeaderField] = customHeaderValue;
    }
  }
  return headers;
}

export interface NewRelicConfig {
  eventApiEndpoint: string;
  apiKey: string;
  allowPostOnSystemAlert: boolean;
}

export interface ClientHooksConfiguration {
  metadata?: string | null;
  folderMetadata?: string | null;
  allowActionHook?: string | null; // used for version creation only right now
  allowSaveOnClientHookError?: boolean;
  customHeaders?: CustomHeadersType;
}

export interface PluginConfig {
  metadata?: string | null;
  // The tab name shown in the UI, if not set, the UI defaults to 'Plugin'
  metadataTitle?: string | null;
  /**
   * If true, the iframe will be reloaded every time the tab is
   * selected or when the currently loaded item changes
   */
  alwaysReloadMetadataPlugin?: boolean | null;
  timeline?: string | null;
  activityPluginUrl?: string | null;
  activityPluginTitle?: string | null;
}

export type NleType = 'premiere' | 'dvr';
export type NleAssetType = 'project' | 'preset';
export interface NleAsset {
  name: string;
  fileName?: string;
  itemId: string;
  nleType?: NleType;
  /**
   * Visibility of the asset.
   *
   * Note:
   * - `undefined`: visible to all groups (everyone)
   * - `empty []`: visible to no groups (no one)
   * - `string[]`: visible to the specified groups
   */
  visibleTo?: string[];

  /**
   * Language string for the subtitle sidecar file(s).
   *
   * When set, Mimir will attempt to collect sidecar subtitle files (e.g., .srt, .stl, .vtt)
   * generated by Adobe Media Encoder (AME) during export, if the Export Preset (EPR)
   * is configured to include captions.
   *
   * These sidecar subtitle files will be attached to the exported video item.
   * If null or undefined, no subtitle sidecar files will be ingested.
   *
   * This setting applies only to Premiere-based workflows (both local and central rendering),
   * and is not relevant for DVR-based projects.
   */
  language?: string | null;
}

export interface SelectedMdfFields {
  formName: string;
  fields: string[];
}

export interface FileNameRulesForExternalSystems {
  maxCharacters: number;
}

export type CustomSearchHookConfiguration = {
  indexUrl: string;
  deleteUrl: string;
  searchUrl: string;
  customHeaders: CustomHeadersType;
  itemsPerPage: number;
  tooltip: string;
};

/**
 * This type defines the tenant config data that is common
 * between the server side, api, and client tenant config types.

 * NOTE: This type was refactored out from a single TenantConfig type that
 * was used across the stack, and so may contain data that is really only
 * relevant server side.
 */
export interface TenantConfigCommon {
  confidenceLevel: number;
  faceDetectionMinimumWidthRatio?: number;
  unknownPersonDetectionConfig?: UnknownPersonDetectionConfig;
  autoAddImagesToDetectedPersons?: boolean;
  translationLanguage: string;
  secondaryTranslationLanguage?: string;
  speakerDiarizationEnabled?: boolean;
  autoTranslationEnabled?: boolean;
  showPersonFunctionality: boolean;
  showLabelFunctionality: boolean;
  showFolderFunctionality: boolean;
  transcodingService?: string;
  editProxyConfig?: EditProxyConfig;
  systemLanguage?: string;
  archiveConfig?: ArchiveConfig;
  paragraphParametricRules?: TranscriptRuleSetting[];
  subtitleParametricRules?: TranscriptRuleSetting[];
  showAudioFunctionality: boolean;
  showFileFunctionality: boolean;
  trintApiKey?: string | null;
  // this started out as settings for realtime trints but is used for regular trints as well
  realtimeTrintConfig?: RealtimeTrintConfig | null;
  recapp2ApiKey?: string | null;
  trintTranscriptOffset?: number | null;
  organizationLabel?: string; // user friendly description for tenant

  clientHooks?: ClientHooksConfiguration;

  forceCreateP2RootItem?: boolean;

  // Determines default analytic settings in the client.
  analyticConfig?: AnalyticConfig;

  // Determines default analytic settings for Premiere rendering
  conformAnalyticConfig?: AnalyticConfig;

  // Determines default analytic settings for recordings
  recordingsAnalyticConfig?: LiveItemAnalytics;

  schedulerMetadataMapping?: SchedulerMetadataMapping;

  // Not visualised in client editable in db only.
  // Temporary, pending discussions on upload view.
  forceCondensedUploadView?: boolean;

  // Metadata defaults mapping
  mdfTypeDefaults: Partial<Record<MetadataTypeDefaults, string>>;

  // Metadata defaults mapping with formData, change to required when all tenants have metadata defaults
  // remove mdfTypeDefaults, once all tenant migrated to metadataDefaults
  metadataDefaults?: Partial<Record<MetadataTypeDefaults, ItemMetaData>>;


  // user groups who can read/write mdf type
  mdfTypePermissions?: MdfTypePermissions;

  // should override with default export mdf during rendering to placeholder
  shouldDefaultExportMdfOverride?: boolean;

  // Per Tenant Service Rates
  serviceRate?: TenantServiceRate;

  // Path to the Mimir folder rendering dependencies should be uploaded to by default
  defaultConformDependenciesFolder?: string;

  /**
   * If enabled, collect source media metadata in the premiere export sequence workflow
  */
  collectSourceMediaMetadata?: boolean;

  // Options for Kelda Live, to enable or disable features
  keldaLiveOpts?: string[];

  // timezone used for presenting all the dates
  tenantWideTimezone?: string | null;

  // time format preference for presenting times: '24h', '12h', or 'system' (use browser default)
  tenantWideTimeFormat?: TenantTimeFormat | null;

  cloudLiveConfig?: CloudLiveConfig;

  defaultMapView?: DefaultMapView;

  createItemsForSubclips?: boolean;

  projectTemplates?: NleAsset[];
  shouldCreateItemForProjectTemplates?: boolean;
  presets?: NleAsset[];
  isMoveDefault?: boolean;
  isEnterForNewLine?: boolean;
  crashRecordingDuration: number;
  crashRecordingName: string;
  defaultCompactViewFields?: Record<string, string[]>;

  plugins?: PluginConfig;
  // A javascript regexp pattern that will be used to filter
  // away all router sources whose names match this pattern
  routerSourceExclusionFilter?: string;
  // Specifies how long before the recording start time of an upcoming series recording
  // we create the Item entity representing the recording. In minutes.
  seriesItemCreationWindowInMins?: number;

  /**
   * Format configuration for subclips generated by MxCoder
   */
  subclipFormatConfig?: MediaFormatConfig;

  // Defines how many milliseconds before a cloud live recording is supposed
  // to start should we initiate the process of setting up the routing crosspoint, if any
  crosspointMarginInMs?: number;

  /**
   * This config used to post an event on system alert to new relic event api
   */
  newRelicConfig?: NewRelicConfig;

  /**
   * If true, MOS messages about a Mimir item will only be sent to
   * the NCS systems that know about this item.
   */
  focusedMosNotifications?: boolean;

  uploadAllPremiereClips?: boolean;

  fileNameRulesForExternalSystems?: FileNameRulesForExternalSystems | null;

  /**
   * ID of the encoding preset to use by default when rendering new sequences.
   */
  defaultEncodingPresetId?: string | null;

  useCutterRendererForSubclips?: boolean;

  /**
   * Note: This variable name is a bit misleading since calling this the
   * *default* subclip preset id indicates that the preset used for subclip
   * can be chosen for each job when in fact this IS the preset that will be
   * used for subclipping.
   */
  defaultSubclipPresetId?: string | null;

  customSearchHookConfig?: CustomSearchHookConfiguration | null;

  hideMimirCloudRenderLocation?: boolean;

  /**
   * Used to enable or disable audio down-mixing.
   * If undefined, it indicates that it has never been used before.
   */
  enableWebProxyAudioDownMix?: boolean;

  /**
   * Information about audio down-mixing for an item.
   * Currently supports stereo audio channel layout.
   *
   * Only takes effect when `enableWebProxyAudioDownMix` is set to `true`.
   */
  webProxyAudioDownMixInfo?: AudioDownMix;

  /**
   * If true, users will be required to use multi-factor authentication.
   *
   * Note:
   *  - Currently, this setting only affects users who log in via email/password.
   *  - External users will not be affected by this setting, e.g., users who log in via SSO.
   */
  enforceMultiFactorAuthentication?: boolean;

  sceneChangeDetectionConfig?: {
    timedMetadataFormId: string;
  };
  blacklistedFileExtensions?: string | null;

  usePremierePreferenceForScaleToFrame?: boolean;
  // If true, the system will display all types of items in the default search.
  // If not true, the system will only display media types in the default search.
  showAllItemTypesInDefaultSearch?: boolean;

  // Default save dialog for cropped images
  defaultCropImageSaveDialog?: CropImageSaveDialogOption;

  // Parameters to tweak for the similar items search (reconnect missing clips)
  // workflow in Premiere/DVR. There is no GUI in the Settings page for this.
  // Needs to be edited from DynamoDB.
  similarItemsSearch?: {
    // Number of milliseconds to extend the range on both sides of the actual
    // duration being sought. Default is 5000. For example, if Premiere
    // reports a duration of 60 seconds, we search for 55 to 65 seconds long
    // media.
    durationTolerance: number;
  };
  // Plugin configuration for the tenant.
  pluginConfig?: PluginDto[];

  // Cutter configuration for the tenant.
  cutterConfig?: CutterConfigDto;
}

/**
 * The tenant config object exposed over API.
 * This may differ from the tenant config entity stored in the db in at least two ways:
 *   - We may fill in default values
 *   - We may fill in data/fields not stored directly in the tenant config
 *     entity but rather derived from other data, such as feature flags.
 *
 */
export interface TenantConfigDto extends TenantConfigCommon {

  // Feature flags defined for this tenant.
  // A FeatureFlag not defined here should assumed to be false.
  featureFlags: Record<FeatureFlag, boolean>;

  customActions: CustomActionDto[];

  customIcons: CustomIconDto[];

  customActionsHeaders: CustomHeadersType;
  customIconRules?: CustomIconRulesDto;

  /**
   * The maximum share limits for this tenant. Set by mimir.
   */
  shareLimits: {
    maxViews: number;
    maxValidDays: number;
  };
  /**
   * The maximum share limits for this tenant. Set by a tenant admin.
   */
  customShareLimits?: {
    /**
     * The maximum number of views allowed for a shared item.
     *
     * If null, the limit is cleared by admin.
     * undefined means value never set.
     */
    maxViews?: number | null;
    /**
     * The maximum number of days a shared item is valid.
     *
     * If null, the limit is cleared by admin.
     * undefined means value never set.
     */
    maxValidDays?: number | null;
  };

  skippedFileExtensionsForPremiere: string[];
}

/**
 * The config that can be specified when creating a new tenant.
 * TODO: Only include fields that make sense on tenant creation;
 *   This used to be typed as TenantConfigDto, and likely contains
 *   fields that do not make sense to specify on tenant creation.
 */
export type CreateTenantConfigDto = TenantConfigCommon;

/**
 * The keys of the TenantConfigDto that can be updated through the REST API.
 */
export const editableTenantConfigKeys: (keyof TenantConfigDto)[] = [
  'confidenceLevel',
  'faceDetectionMinimumWidthRatio',
  'unknownPersonDetectionConfig',
  'autoAddImagesToDetectedPersons',
  'translationLanguage',
  'secondaryTranslationLanguage',
  'speakerDiarizationEnabled',
  'autoTranslationEnabled',
  'showPersonFunctionality',
  'showAudioFunctionality',
  'showFileFunctionality',
  'showLabelFunctionality',
  'showFolderFunctionality',
  'paragraphParametricRules',
  'subtitleParametricRules',
  'editProxyConfig',
  'forceCreateP2RootItem',
  'analyticConfig',
  'conformAnalyticConfig',
  'recordingsAnalyticConfig',
  'mdfTypeDefaults',
  'mdfTypePermissions',
  'focusedMosNotifications',
  'shouldDefaultExportMdfOverride',
  'trintApiKey',
  'realtimeTrintConfig',
  'recapp2ApiKey',
  'defaultConformDependenciesFolder',
  'collectSourceMediaMetadata',
  'defaultMapView',
  'tenantWideTimezone',
  'tenantWideTimeFormat',
  'projectTemplates',
  'shouldCreateItemForProjectTemplates',
  'createItemsForSubclips',
  'presets',
  'clientHooks',
  'isMoveDefault',
  'isEnterForNewLine',
  'organizationLabel',
  'crashRecordingDuration',
  'crashRecordingName',
  'subclipFormatConfig',
  'plugins',
  'defaultCompactViewFields',
  'newRelicConfig',
  'uploadAllPremiereClips',
  'fileNameRulesForExternalSystems',
  'customActionsHeaders',
  'defaultEncodingPresetId',
  'useCutterRendererForSubclips',
  'defaultSubclipPresetId',
  'metadataDefaults',
  'customSearchHookConfig',
  'hideMimirCloudRenderLocation',
  'enableWebProxyAudioDownMix',
  'webProxyAudioDownMixInfo',
  'enforceMultiFactorAuthentication',
  'usePremierePreferenceForScaleToFrame',
  'blacklistedFileExtensions',
  'skippedFileExtensionsForPremiere',
  'customShareLimits',
  'showAllItemTypesInDefaultSearch',
  'customIconRules',
  'cutterConfig',
  'defaultCropImageSaveDialog'
];

export type UpdateTenantConfigDto = Partial<Pick<TenantConfigDto, typeof editableTenantConfigKeys[number]>>;


/**
 * Describes the different types of configurable defaults, where we attempt to preselect an MDF type if
 * configured in this mapping. Mapping should be one of the below values to MDF id.
 */
export type MetadataTypeDefaults = 'image' | 'video' | 'audio' | 'file' | 'recording' | 'export' | 'mimir-sequence';

/**
 * read: user groups which can view the mdf type dropdown (not an api level thing, only affects visibility in client)
 * write: user groups which can modify the mdf type of an item
 */
export type MdfTypePermissions = { read?: string[], write?: string[] };


export interface AnalyticConfig {
  transcriptionEnabled: boolean;
  labelDetectionEnabled: boolean;
  textDetectionEnabled: boolean;
  personDetectionEnabled: boolean;
  celebrityDetectionEnabled: boolean;

  // Only relevant when transcription is enabled
  transcriptionConfig?: ItemTranscriptionSettings;
  // Only relevant when person detection is enabled
  personDetectionConfig?: PersonDetectionParams;
  // Only relevant when text detection is enabled
  textDetectionSettings?: TextDetectionSettings;

  analyticPlugins: Record<string, AnalyticsPluginConfig> | undefined;
}

export interface UploadConfig extends AnalyticConfig {
  folderId?: string;
  targetUploadLocationId?: string;
  uploadFolderPath?: string;
}

// The analytics that are supported on cloud live items.
export type LiveItemAnalytics = CompleteType<Partial<AnalyticConfig>>;


// When creating a scheduled recording from an existing placeholder Item,
// these values are used to map the Item's metadata to recording params.
// All field references here use readable fieldIds.
export interface SchedulerMetadataMapping {
  startTimeField?: string;
  endTimeField?: string;
}

export interface ProxyFormatInfo {
  extension: string;
  contentType: string;
  configProps: (keyof EditProxyConfig)[];
  displayName: string;
}

export const getProxyFormatInfo = (configProps: Record<CloudEncodingFormat, (keyof EditProxyConfig)[]>): Record<CloudEncodingFormat, ProxyFormatInfo> => ({
  'h264': {
    extension: '.mp4',
    contentType: 'video/mp4',
    configProps: configProps.h264,
    displayName: 'H.264'
  },
  'prores': {
    extension: '.mov',
    contentType: 'video/quicktime',
    configProps: configProps.prores,
    displayName: 'ProRes 422 Proxy'
  },
  'mpegts': {
    extension: '.ts',
    contentType: 'video/mp2t',
    configProps: configProps.mpegts,
    displayName: 'MPEG TS'
  }
});

export const proxyFormats = getProxyFormatInfo({
  'h264': ['enabled', 'profile', 'bitrate', 'maxBitrate', 'audioBitrate', 'fps', 'gopSize', 'par', 'maxHeight', 'format', 'mergeAudioStreams', 'alphaResolutionMode'],
  'prores': ['enabled', 'audioBitrate', 'fps', 'par', 'maxHeight', 'format', 'mergeAudioStreams', 'alphaResolutionMode'],
  // Since this edit proxy format is not something users can choose,
  // we're using an empty property array so that it doesn't show up in the UI.
  'mpegts': []
});

export const subclipFormats = getProxyFormatInfo({
  h264: ['enabled', 'fps', 'maxHeight', 'format', 'par', 'fieldOrder'],
  prores: [],
  mpegts: []
});

export const getDefaultSubclipFormatConfig = (): MediaFormatConfig => {
  return {
    enabled: false,
  };
};

export type AlphaResolutionMode = 'quarter' | 'source';

export interface MediaFormatConfig {
  enabled?: boolean;
  profile?: string | null;
  bitrate?: number;
  maxBitrate?: number;
  audioBitrate?: number;
  fps?: typeof editProxyFpss[number]['key'] | null;
  gopSize?: number;
  par?: string | null;
  maxHeight?: number | null;
  format?: EditProxyFormat;
  mergeAudioStreams?: boolean;
  preset?: H264Preset;
  crf?: number;
  fieldOrder?: typeof SupportedVideoConversionFieldOrders[number]['value']; // only used in cloud subclips
  alphaResolutionMode?: AlphaResolutionMode;
}

// mpegts is declared an edit proxy format because we can
// use .ts segments produced by cloud live ingest, but at least
// for now, we do not allow generating ts edit proxies for non
// live ingests, which is why we filter this value out of the
// choices offered in the tenant configuration UI
export const supportedEditProxyFormats = (Object.keys(proxyFormats) as EditProxyFormat[]).filter(f => f !== 'mpegts');

export const supportedCloudSubclipFormats: CloudEncodingFormat[] = ['h264'];

export type EditProxyConfig = MediaFormatConfig;

export type TransitionAudioMode = 'overlap' | 'back-to-back' | 'preserve-audio';
export type TransitionEffectType = 'dissolve';
export interface TransitionPreset {
  presetId: string;
  label: string;
  duration: Fraction;
  type: TransitionEffectType;
  audioTransitionMode: TransitionAudioMode;
}

export interface CutterConfigDto {
  // Other cutter config properties can be added here later
  transitionPresets: TransitionPreset[] | null;
}

export const defaultCutterConfig = (): CutterConfigDto => {
  return {
    transitionPresets: null,
  };
};

export type RealtimeTrintConfig = {
  /**
   * Optional parameter that specifies a workspace where realtime trint will be created
   */
  workspaceId?: string | null;
  /**
   * Optional parameter that specifies a folder inside a workspace where realtime trint will be created
   */
  folderId?: string | null;

  domain?: string | null;
};

/**
 * Config values that affect the outcome of the unknown-person operation.
 * Note: When matching faces against the tenant's person collection we use
 * the same confidence level as for the standard Face Detection operation, so this
 * value is not configured here.
 */
export interface UnknownPersonDetectionConfig {
  /**
   * Determines how much to expand the face bounding box when creating
   * a person image from a detected face.
   * 0 means no zoom out, which will result in an image very tight around the core face,
   * while e.g., 100 gives a face image with width and height doubled from the bounding box dimensions.
   */
  zoomOutPercentage: number;

  /**
   * The threshold determining how confident we need to be that a
   * detected face is actually a face, in order to consider using it
   * for unknown person detection.
   * This value will typically be high, >99, since we want to be very sure
   * we have an actual face if we're using that face/image for creating a new person.
   */
  faceDetectionConfidence: number;

  /**
   * The minimum width, as a ratio of the media's width, the detected face bounding box
   * needs to be. A larger value will cause more faces to be filtered away for not being
   * prominent enough.
   * This setting applies to the face bounding box, and not the later extracted face image.
   */
  boundingBoxMinimumWidthRatio: number;

  /**
   * When a face image is matched against an existing person, these settings
   * determine if we add the image to the person's image collection.
   */
  autoAddImageConfig: {

    /**
     * Add only if the person has fewer existing images than this value.
     */
    imageCountThreshold: number,

    /**
     * Add only if the match similarity was at least this high. In [0, 100].
     */
    similarityThreshold: number;

    /**
     * Add only if the detected face bounding box has a width
     * ratio at least this large.
     */
    boundingBoxMinimumWidthRatio: number;
  }
}

export const defaultUnknownPersonConfig = (): UnknownPersonDetectionConfig => {
  return {
    zoomOutPercentage: 60,
    faceDetectionConfidence: 99.5,
    boundingBoxMinimumWidthRatio: 0.10,
    autoAddImageConfig: {
      imageCountThreshold: 5,
      boundingBoxMinimumWidthRatio: 0.10,
      similarityThreshold: 95,
    }
  };
};

export const defaultArchiveConfig = (): ArchiveConfig => {
  return {
    coldPeriodInDaysForGlacier: 30,
    coldPeriodInDaysForDeepArchive: 365,
    minimumFileSizeInMB: 100,
    defaultRestoreTier: RestoreTier.Expedited,
    defaultRetentionDays: 5,
  };
};

export interface ArchiveConfig {
  /**
   * Move the media to Glacier after this configured number of days
   */
  coldPeriodInDaysForGlacier: number,
  /**
   * Move to Deep Archive after this configured number of days
   */
  coldPeriodInDaysForDeepArchive: number,
  /**
   * Minimum size of the file to archive
   */
  minimumFileSizeInMB: number,
  /**
   * Retention days will be used while automatically restore and download a media
   */
  defaultRetentionDays: number,
  /**
   * Restore tier will be used while automatically restore and download a media
   */
  defaultRestoreTier: RestoreTier,
}

export const isInstanceOfArchiveConfig = (obj: unknown): obj is ArchiveConfig => {
  if (isObject(obj)
    && 'coldPeriodInDaysForGlacier' in obj
    && isNumber(obj['coldPeriodInDaysForGlacier'])
    && 'coldPeriodInDaysForDeepArchive' in obj
    && isNumber(obj['coldPeriodInDaysForDeepArchive'])
    && 'minimumFileSizeInMB' in obj
    && isNumber(obj['minimumFileSizeInMB'])
    && 'defaultRetentionDays' in obj
    && isNumber(obj['defaultRetentionDays'])
    && 'defaultRestoreTier' in obj
    && isEnum(RestoreTier)(obj['defaultRestoreTier'])) {
    return true;
  }

  return false;
};

/**
 * Determines whether the ArchiveConfig configuration is valid or not
 * @description
 * Configured days for coldPeriodInDaysForGlacier must be defined or greater than 0.
 * Configured days for coldPeriodInDaysForDeepArchive must be defined or greater than 0.
 * Configured days for coldPeriodInDaysForDeepArchive must be greater than coldPeriodInDaysForGlacier.
 * Minimum file size to archive must be greater than 100
 * @param payload - Object of ArchiveConfig
 */
export const isValidArchiveConfig = (payload: ArchiveConfig | undefined) => {
  if (payload
    && payload.coldPeriodInDaysForGlacier > 0
    && payload.coldPeriodInDaysForDeepArchive > 0
    && payload.coldPeriodInDaysForDeepArchive > payload.coldPeriodInDaysForGlacier
    && payload.minimumFileSizeInMB >= 100
    && payload.defaultRetentionDays > 0
    && payload.defaultRestoreTier
    && isEnum(RestoreTier)(payload.defaultRestoreTier)
  ) {
    return true;
  }

  return false;
};

export const isInstanceOfAvailability = (payload: unknown): payload is Availability => {
  if (isObject(payload) && 'data' in payload) return true;
  return false;
};


export const validateEditProxyEnabled = (v: unknown): boolean | string => {
  if (v === undefined || (typeof v === 'boolean')) {
    return true;
  }

  return 'editProxyConfig enabled field must be boolean';
};

export const validateEditProxyMergeAudioStreams = (v: unknown): boolean | string => {
  if (v === undefined || (typeof v === 'boolean')) {
    return true;
  }

  return 'editProxyConfig mergeAudioStreams field must be boolean';
};

export const h264Profiles = [
  'baseline',
  'main',
  'high',
];

export const validateEditProxyFormat = (v: unknown): boolean | string => {
  if (v === undefined ||
      v === null ||
      v === '' ||
      ((typeof v === 'string') && supportedEditProxyFormats.includes(v as any))) {
    return true;
  }

  return `Not a valid edit-proxy format: ${JSON.stringify(v)}`;
};

export const validateEditProxyProfile = (v: unknown): boolean | string => {
  if (v === undefined ||
      v === null ||
      v === '' ||
      ((typeof v === 'string') && h264Profiles.includes(v as string))) {
    return true;
  }

  return `Not a valid H.264 profile: ${JSON.stringify(v)}`;
};

export const editProxyPars = [
  { label: 'Square (1.0)', key: '1/1' }
];

export const validateEditProxyPar = (v: unknown): boolean | string => {
  if (v === undefined ||
      v === null ||
      v === '' ||
      v === 'auto' || // backwards compatibility. We have values in the db containing auto due to old configurations setting this.
      ((typeof v === 'string') && editProxyPars.find(par => par.key === v))) {
    return true;
  }

  return `PAR not supported: ${JSON.stringify(v)}`;
};



const validateRange = (v: unknown, min: number, max: number, divider?: 'mbit' | 'kbit'): boolean | string => {
  if (v === undefined ||
      v === null ||
      ((typeof v === 'number') && v >= min &&  v <= max)) {
    return true;
  }
  let dividerValue = 1;
  if (divider) {
    dividerValue = divider === 'mbit' ? 1e6 : 1000;
  }
  return `Must be between ${min / dividerValue} to ${max / dividerValue}`;
};

const bitrateMin = 1000;
const bitrateMax = 5e7;
export const validateEditProxyBitrate = (v: unknown): boolean | string => {
  return validateRange(v, bitrateMin, bitrateMax, 'mbit');
};

const audioBitrateMin = 1000;
const audioBitrateMax = 2e6;
export const validateEditProxyAudioBitrate = (v: unknown): boolean | string => {
  return validateRange(v, audioBitrateMin, audioBitrateMax, 'kbit');
};

const gopSizeMin = 1;
const gopSizeMax = 300;
export const validateEditProxyGopSize = (v: unknown): boolean | string => {
  if (v === undefined ||
      v === null ||
      ((typeof v === 'number') && v >= gopSizeMin &&  v <= gopSizeMax)) {
    return true;
  }

  return `Must be between ${gopSizeMin} to ${gopSizeMax}`;
};

export const editProxyMaxHeights = [
  720,
  1080
];

export const validateEditProxyMaxHeight = (v: unknown): boolean | string => {
  if (v === undefined ||
      v === null ||
      v === '' ||
      ((typeof v === 'number') && editProxyMaxHeights.includes(v))) {
    return true;
  }

  return `Height not supported: ${JSON.stringify(v)}`;
};

export const validateEditProxyField = (field: keyof EditProxyConfig, v: unknown): boolean | string => {
  let validationFn: undefined | ((v: unknown) => boolean | string) = undefined;
  switch (field) {
    case 'enabled':
      validationFn = validateEditProxyEnabled;
      break;
    case 'format':
      validationFn = validateEditProxyFormat;
      break;
    case 'profile':
      validationFn = validateEditProxyProfile;
      break;
    case 'bitrate':
      validationFn = validateEditProxyBitrate;
      break;
    case 'maxBitrate':
      validationFn = validateEditProxyBitrate;
      break;
    case 'audioBitrate':
      validationFn = validateEditProxyAudioBitrate;
      break;
    case 'fps':
      validationFn = validateEditProxyFps;
      break;
    case 'gopSize':
      validationFn = validateEditProxyGopSize;
      break;
    case 'par':
      validationFn = validateEditProxyPar;
      break;
    case 'maxHeight':
      validationFn = validateEditProxyMaxHeight;
      break;
    case 'mergeAudioStreams':
      validationFn = validateEditProxyMergeAudioStreams;
      break;
  }

  if (!validationFn) {
    return false;
  }

  return validationFn(v);
};

function optionalRangeValueValidator(min: number, max: number) {
  return (value: string | number) => {
    if (value === '') {
      return true;
    }
    const v = typeof value === 'string' ? parseInt(value, 10) : value;
    return validateRange(v, min, max);
  };
}

export const unknownPersonValidators = {
  zoomOut: optionalRangeValueValidator(0, 200),
  boundingBoxWidth: optionalRangeValueValidator(0.05, 0.75),
  boundingBoxWidthUI: optionalRangeValueValidator(5, 75),
  confidence: optionalRangeValueValidator(0, 100),
  imageCountThreshold: optionalRangeValueValidator(1, 20),
};

export function validateUnknownPersonConfig(config: UnknownPersonDetectionConfig): boolean | string {
  config.autoAddImageConfig = config.autoAddImageConfig ?? defaultUnknownPersonConfig().autoAddImageConfig;
  const validations = [
    unknownPersonValidators.zoomOut(config.zoomOutPercentage),
    unknownPersonValidators.boundingBoxWidth(config.boundingBoxMinimumWidthRatio),
    unknownPersonValidators.boundingBoxWidth(config.autoAddImageConfig.boundingBoxMinimumWidthRatio),
    unknownPersonValidators.confidence(config.faceDetectionConfidence),
    unknownPersonValidators.confidence(config.autoAddImageConfig.similarityThreshold),
    unknownPersonValidators.imageCountThreshold(config.autoAddImageConfig.imageCountThreshold)
  ];
  return validations.find(res => typeof res === 'string') ?? true;
}

export const validateCustomShareLimits = (
  limits: TenantConfigDto['customShareLimits'],
  maxLimit: TenantConfigDto['shareLimits'],
): true | string => {
  if (!limits) return true;

  const { maxViews, maxValidDays } = limits;

  if (maxViews != undefined) {
    if (typeof maxViews !== 'number' || maxViews <= 0) {
      return 'maxViews must be a positive number';
    }
    if (maxViews > maxLimit.maxViews) {
      return `maxViews cannot exceed the maximum limit of ${maxLimit.maxViews}`;
    }
  }

  if (maxValidDays != undefined) {
    if (typeof maxValidDays !== 'number' || maxValidDays <= 0) {
      return 'maxValidDays must be a positive number';
    }
    if (maxValidDays > maxLimit.maxValidDays) {
      return `maxValidDays cannot exceed the maximum limit of ${maxLimit.maxValidDays}`;
    }
  }

  return true;
};

export const validateTransitionPreset = (preset: TransitionPreset): boolean | string => {
  if (!preset.presetId || typeof preset.presetId !== 'string') {
    return 'presetId must be a non-empty string';
  }

  if (!preset.label || typeof preset.label !== 'string') {
    return 'label must be a non-empty string';
  }

  if (!isInstanceOfFraction(preset.duration) || preset.duration.denominator === 0) {
    return 'duration must be a valid Fraction with non-zero denominator';
  }

  const transitionType = preset.type;
  switch (transitionType) {
    case 'dissolve': break;

    default: transitionType satisfies never; return 'type must be a valid transition effect type';
  }

  const audioTransitionMode = preset.audioTransitionMode;
  switch (audioTransitionMode) {
    case 'overlap':
    case 'back-to-back':
    case 'preserve-audio': break;

    default: audioTransitionMode satisfies never; return 'audioTransitionMode must be a valid audio transition mode';
  }

  return true;
};

export const validateCutterConfigDto = (config: CutterConfigDto): boolean | string => {
  if (config.transitionPresets === null) {
    return true;
  }

  if (!Array.isArray(config.transitionPresets)) {
    return 'transitionPresets must be an array or null';
  }

  const presetIds = new Set<string>();

  for (const preset of config.transitionPresets) {
    const validationResult = validateTransitionPreset(preset);
    if (validationResult !== true) {
      return validationResult;
    }

    if (presetIds.has(preset.presetId)) {
      return `Duplicate presetId: ${preset.presetId}`;
    }
    presetIds.add(preset.presetId);
  }

  return true;
};

const slotRuleDtoValidator = makeValidationFunction<CustomIconSlotRuleDto | null>((input) => {
  if (input === null) {
    return null;
  }
  const iconFieldId = validateStringProperty(input, 'iconFieldId');
  const tooltipFieldId = validateOptionalStringProperty(input, 'tooltipFieldId');
  return {
    iconFieldId,
    ...(tooltipFieldId ? { tooltipFieldId } : {})
  };
});

export const customIconRulesDtoValidator = makeValidationFunction<CustomIconRulesDto>((input: any) => {
  if (!input || typeof input !== 'object') {
    throw 'Invalid input: expected an object';
  }
  const result: CustomIconRulesDto = {} as CustomIconRulesDto;

  itemCustomIconSlots.forEach(pos => {
    const validationResult = slotRuleDtoValidator(input[pos]);
    if (validationResult.errorMsg != null) {
      result[pos] = null;
    } else {
      result[pos] = validationResult.value;
    }
  });

  return result;
});
